import React from 'react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import FlightsDetails from 'components/FlightsDetails';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { ConfirmMessage } from 'models/confirmMessage';
import { FlightValidationResponse } from 'models/flightValidation';
import SolicitationSuccess from 'screens/components/SolicitationSuccess';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { htmlT } from 'utils/generic';

import ConfirmApproveModal from '../Dashboard/components/ConfirmApproveModal';
import CancellationPolicies from './components/CancellationPolicies';
import Checkout from './components/Checkout';
import Price from './components/Price';
import { Container, Body, SectionContainer } from './styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Tooltip from 'components/Tooltips/Tooltip';

export interface Props {
  show: boolean;
  creating: boolean;
  loading: boolean;
  details?: any;
  isBySector?: boolean;
  onConfirm: (
    flight: FlightValidationResponse,
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
    card?: string
  ) => void;
  onClose: () => void;
  showChurning: boolean;
  setShowChurning: (show: boolean) => void;
  //showSuccess: boolean;
  travelers?: TravelArrangerUsersAllowed[];
  showConfirmFlow: boolean;
  confirmFlowMessage: ConfirmMessage;
  onConfirmFlow: () => void;
  onConfirmFlowClose: () => void;
  zIndex?: number;
}

const FlightDetails = ({
  show,
  creating,
  loading,
  details,
  onClose,
  onConfirm,
  showChurning,
  setShowChurning,
  //showSuccess,
  travelers,
  onConfirmFlow,
  showConfirmFlow,
  confirmFlowMessage,
  onConfirmFlowClose,
  zIndex,
  isBySector
}: Props) => {
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();
  const isConfirmationSuccess = useSelector((state: RootState) => state.flights.confirmationSuccess);
  const isConfirmationError = useSelector((state: RootState) => state.flights.confirmationError);
  const travel_itinerary_id = useSelector((state: RootState) => state.travel.travelItineraryId);

  const handleConfirm = (
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
    card?: string
  ) => {
    if (!details && !isBySector) return;
    onConfirm(details, justifyForBreakingRules, reason, costCenterId, breakRulesMessageId, card);
  };

  const headerSubtitleTooltip = () => {
    if (travelers.length > 1) {
      return (
    <Tooltip
          whiteColor={true}
          event="hover"
          content={
            getTooltipContent()
          }
        >
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span>
            {htmlT(t, 'flightsSearch.toTraveler', {
              user: getTravelersNames(),
            })}
          </span> &nbsp; 
          <img width={20} height={20} src="/corporate/images/advice-2.svg" alt="" />
        </div>
      </Tooltip>
      );
    } else {
      return (
        <span style={{ display: 'inline'}}>
          {htmlT(t, 'flightsSearch.toTraveler', { user: getTravelersNames()})}
        </span>
      );
    }    
  };

  const getTooltipContent = () => (
    <>
      <div style={{ marginBottom: 18, fontWeight: 600 }}>
        {t('flightsSearch.additionalTraveler')}
      </div>
      {travelers.slice(1).map((t, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <img src="/corporate/images/check-copy.svg" alt="" width={14} height={14} />
          <span>
            {t.name} {t.lastName}
          </span>
        </div>
      ))}
    </>
  );

  const getTravelersNames = () => (
    `<span style='color: #F4BF31; font-weight: 600; font-size: 16px; padding-left: 4px; display: contents'>
      ${travelers[0]?.name} ${travelers[0]?.lastName} ${
        travelers.length > 1 ? '+ ' + (travelers.length - 1) : ''
      }
    </span>`
  );

  const renderContent = () => {
    if (details && !isBySector) {
      return (
        <>
          <Body>
            <FlightsDetails breakRulesMessage={details.breakRulesMessage} sectors={details.tripOption.sectors} />
            <SectionContainer>
              <CancellationPolicies policies={details.airFareConditions} />
              <Price
                travelers={travelers && travelers.length > 0 ? travelers.length : 1}
                price={details.tripOption.price}
                alternativePrice={details.tripOption.alternativePrice}
                details={details}
              />
            </SectionContainer>
            <br />
            <br />
            <Checkout
              loading={creating}
              isBestOption={false}
              onConfirm={handleConfirm}
              onClose={onClose}
              price={details.tripOption.price}
              isPreApproved={Boolean(details.isPreApproved)}
              showChurning={showChurning}
              setShowChurning={setShowChurning}
              details={details}
              alternativePrice={details.tripOption.alternativePrice}
            />
          </Body>
           <SolicitationSuccess
            show={isConfirmationSuccess}
            isHotel={false}
            isFlight={true}
            testId="flight-solicitation-success"
            tid={travel_itinerary_id}
          /> 
        </>
      );
    }
    if (details && isBySector) {
      return (
        <>
          <Body >
            {details.airValidateResponse.map((item: FlightValidationResponse, index: number) => (
              <>
                <FlightsDetails key={index} breakRulesMessage={details.breakRulesMessage} sectors={item.tripOption.sectors} />
                <SectionContainer>
                  <CancellationPolicies policies={item.airFareConditions} />
                </SectionContainer>
              </>
            ))}
          </Body>
          <Price
            travelers={travelers && travelers.length > 0 ? travelers.length : 1}
            price={details.totalPrice}
            alternativePrice={details.totalPrice.alternativePrice}
            details={details}
            isBySector={true}
          />
          <Checkout
            loading={creating}
            isBestOption={false}
            onConfirm={handleConfirm}
            onClose={onClose}
            price={details.totalPrice}
            isPreApproved={Boolean(details.isPreApproved)}
            showChurning={showChurning}
            setShowChurning={setShowChurning}
            details={details}
            alternativePrice={details.alternativeTotalPrice}
            isBySector={true}
          />
        </>
      )
    }
  };

  return (
    <>
      <ApplicationOverlayContent show={show} width={600} onClose={onClose} testId="flight-details" zIndex={zIndex}>
        <Container>
          <Mobile>
            <AppBar goBack={onClose}>{t('flightDetails.title')}</AppBar>
          </Mobile>

          <Desktop>
            <ModalHeader
              onClose={onClose}
              title={t('flightDetails.title')}
              subtitle={
                travelers ? (
                  <>
                    {headerSubtitleTooltip()}
                  </>
                ) : undefined
              }
            />
          </Desktop>

          {loading ? <Loader fullScreen={isMobile} /> : renderContent()}

        </Container>
      </ApplicationOverlayContent>
      
      <SolicitationSuccess
            show={isConfirmationSuccess}
            isHotel={false}
            isFlight={true}
            testId="flight-solicitation-success"
            tid={travel_itinerary_id}
          />
      <ConfirmApproveModal
        onConfirmApprove={onConfirmFlow}
        onConfirmClose={onConfirmFlowClose}
        showConfirm={showConfirmFlow}
        confirmMessage={confirmFlowMessage}
        showCancelButton={false}
      ></ConfirmApproveModal>
      {/*       <ApplicationOverlayContent show={true} width={600} onClose={onClose} testId="flight-details" zIndex={999}>
        <Container>
          <Mobile>
            <AppBar>{t('flightDetails.title')}</AppBar>
          </Mobile>

          <Desktop>
            <ModalHeader onClose={onClose} title={'Sesión expirada en Travel Corporate'} subtitle={''} />
            <p>Hola Adriana Olaya, tu sesión ha expirado! </p>
            <p>Vamos redireccionar a la página de Login para que vuelvas a ingresar en la plataforma de Travel Corporate.</p>
            
          </Desktop>
        </Container>
      </ApplicationOverlayContent> */}
    </>
  );
};

export default FlightDetails;
